/**
 * Einheitliche Auswertung von Anruf-Aktivitäten (FloatingCall speichert outcome als Slug).
 * Unterstützt alte Datensätze mit deutschsprachigem oder freiem outcome-Text.
 */

export const CALL_OUTCOME_SLUGS = [
  'reached_appt',
  'reached_followup',
  'reached_no',
  'not_voicemail',
  'not_busy',
  'not_noanswer',
  'gatekeeper',
  'callback',
  'decision_maker',
];

const SLUG_SET = new Set(CALL_OUTCOME_SLUGS);

/** Anzeige pro Slug (wenn kein outcomeLabel am Datensatz) */
export const CALL_OUTCOME_LABELS = {
  reached_appt:     'Termin gesetzt',
  reached_followup: 'Follow-up nötig',
  reached_no:       'Kein Interesse',
  not_voicemail:    'Voicemail',
  not_busy:         'Besetzt',
  not_noanswer:     'Kein Anschluss',
  gatekeeper:       'Gatekeeper',
  callback:         'Rückruf vereinbart',
  decision_maker:   'Entscheider erreicht',
  unknown:          'Ohne Ergebnis',
};

/**
 * @param {{ outcome?: string, outcomeLabel?: string }} act
 * @returns {string}
 */
export function normalizeCallOutcomeSlug(act) {
  const raw = (act?.outcome ?? '').toString().trim();
  if (!raw) return 'unknown';
  if (SLUG_SET.has(raw)) return raw;
  const l = raw.toLowerCase();
  if (l.includes('termin') && (l.includes('gesetzt') || l.includes('vereinbart') || l === 'terminiert' || l.includes('terminiert'))) {
    return 'reached_appt';
  }
  if (l.includes('gatekeeper')) return 'gatekeeper';
  if (l.includes('entscheid') || l.includes('decision') || l.includes('geschäftsführ')) return 'decision_maker';
  if (l.includes('voicemail') || l.includes('mailbox')) return 'not_voicemail';
  if (l.includes('besetzt')) return 'not_busy';
  if (l.includes('kein anschluss') || l.includes('noanswer') || l.includes('nicht erreicht')) return 'not_noanswer';
  if (l.includes('rückruf') || l.includes('callback')) return 'callback';
  if (l.includes('kein interesse') || l.includes('kein bedarf')) return 'reached_no';
  if (l.includes('follow')) return 'reached_followup';
  if (l.includes('termin') && l.includes('✓')) return 'reached_appt';
  return 'unknown';
}

export function displayCallOutcome(act) {
  if (act?.outcomeLabel) return act.outcomeLabel;
  const slug = normalizeCallOutcomeSlug(act);
  return CALL_OUTCOME_LABELS[slug] || act?.outcome || '–';
}

/** Nur Anrufe zählen, die per „Anruf speichern“ protokolliert wurden (nicht abgebrochen). */
export function isTrackedCall(act) {
  if (!act || act.type !== 'call') return false;
  if (act.outcomeSaved === true) return true;
  if (act.outcomeSaved === false) return false;
  return normalizeCallOutcomeSlug(act) !== 'unknown';
}

function norm(s) {
  return (s || '').toString().trim().toLowerCase();
}

/**
 * Passt ein Anruf zum gewählten CRM-Nutzer (Admin-Filter)?
 * @param {string} userId - 'all' oder profiles.id
 */
export function callMatchesUser(call, userId, authUsers = []) {
  if (!userId || userId === 'all') return true;
  if (call.byUserId && String(call.byUserId) === String(userId)) return true;
  const u = authUsers.find(x => String(x.id) === String(userId));
  if (!u) return false;
  const by = norm(call.by);
  if (!by) return false;
  if (norm(u.name) && by === norm(u.name)) return true;
  if (norm(u.email) && by === norm(u.email)) return true;
  if (norm(u.name) && by.includes(norm(u.name))) return true;
  return false;
}

/**
 * Kennzahlen für eine gefilterte Anrufliste (nur type === 'call', bereits nach Zeitraum gefiltert).
 */
export function aggregateCallMetrics(calls) {
  const m = {
    total: 0,
    termin: 0,
    gatekeeper: 0,
    decider: 0,
    followup: 0,
    noInterest: 0,
    voicemail: 0,
    busy: 0,
    noAnswer: 0,
    callback: 0,
    unknown: 0,
  };
  for (const c of calls) {
    if (!isTrackedCall(c)) continue;
    m.total += 1;
    const s = normalizeCallOutcomeSlug(c);
    switch (s) {
      case 'reached_appt': m.termin += 1; break;
      case 'gatekeeper': m.gatekeeper += 1; break;
      case 'decision_maker': m.decider += 1; break;
      case 'reached_followup': m.followup += 1; break;
      case 'reached_no': m.noInterest += 1; break;
      case 'not_voicemail': m.voicemail += 1; break;
      case 'not_busy': m.busy += 1; break;
      case 'not_noanswer': m.noAnswer += 1; break;
      case 'callback': m.callback += 1; break;
      default: m.unknown += 1; break;
    }
  }
  const pct = (x) => (m.total > 0 ? Math.round((x / m.total) * 100) : 0);
  const notReached = m.voicemail + m.busy + m.noAnswer;
  const reached = Math.max(0, m.total - notReached);
  return {
    ...m,
    notReached,
    reached,
    pctTermin: pct(m.termin),
    pctGate: pct(m.gatekeeper),
    pctDecider: pct(m.decider),
    pctFollowup: pct(m.followup),
    pctCallback: pct(m.callback),
    pctNoInterest: pct(m.noInterest),
    pctTerminVsReached: reached > 0 ? Math.round((m.termin / reached) * 100) : 0,
  };
}
